import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Upload, ArrowLeft, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { fetchProductImage } from "@/lib/productImages.functions";
import {
  emptyProduct,
  listProducts,
  parseProductCsv,
  parseProductLibraryXml,
  upsertProducts,
  type Product,
  type ProductDraft,
} from "@/lib/products";

export const Route = createFileRoute("/tools/products")({
  head: () => ({ meta: [{ title: "Products" }, { name: "robots", content: "noindex" }] }),
  component: ProductsPage,
});

function ProductsPage() {
  const [rows, setRows] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<(ProductDraft & { id?: string }) | null>(null);
  const [fetchingImage, setFetchingImage] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const findImage = useServerFn(fetchProductImage);

  const load = async (term = search) => {
    setLoading(true);
    try {
      setRows(await listProducts(term, 300));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { load(search); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleFile = async (file: File) => {
    setImporting(true);
    try {
      const text = await file.text();
      const isXml = /\.xml$/i.test(file.name) || text.trimStart().startsWith("<");
      const parsed = isXml ? parseProductLibraryXml(text) : parseProductCsv(text);
      if (parsed.length === 0) throw new Error("No products found in that file.");
      const saved = await upsertProducts(parsed);
      toast.success(`Imported ${saved} product${saved === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.model.trim()) return toast.error("Model / SKU is required.");
    const { id, ...draft } = editing;
    const payload = {
      ...draft,
      markup_pct:
        draft.unit_cost > 0
          ? Math.round(((draft.sell_price / draft.unit_cost - 1) * 100 + Number.EPSILON) * 100) / 100
          : null,
    };
    const { error } = id
      ? await supabase.from("products").update(payload as never).eq("id", id)
      : await supabase.from("products").insert(payload as never);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setEditing(null);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  if (editing) {
    return (
      <div className="max-w-2xl">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => setEditing(null)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Model / SKU">
            <Input value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} />
          </Field>
          <Field label="Manufacturer">
            <Input value={editing.mfg_name || ""} onChange={(e) => setEditing({ ...editing, mfg_name: e.target.value || null })} />
          </Field>
          <Field label="Supplier">
            <Input value={editing.supplier} onChange={(e) => setEditing({ ...editing, supplier: e.target.value })} />
          </Field>
          <Field label="Category">
            <Input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value || null })} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description">
              <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field>
          </div>
          <Field label="Unit cost">
            <NumberInput value={editing.unit_cost} onValueChange={(n) => setEditing({ ...editing, unit_cost: n })} />
          </Field>
          <Field label="Sell price">
            <NumberInput value={editing.sell_price} onValueChange={(n) => setEditing({ ...editing, sell_price: n })} />
          </Field>
          <Field label="Install phase">
            <Input value={editing.phase || ""} onChange={(e) => setEditing({ ...editing, phase: e.target.value || null })} />
          </Field>
          <Field label="Product URL">
            <Input value={editing.product_url || ""} onChange={(e) => setEditing({ ...editing, product_url: e.target.value || null })} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Product image URL">
              <div className="flex gap-2">
                <Input
                  placeholder="https://…/product.jpg"
                  value={editing.image_url || ""}
                  onChange={(e) => setEditing({ ...editing, image_url: e.target.value || null })}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!editing.id || fetchingImage}
                  onClick={async () => {
                    if (!editing.id) return;
                    setFetchingImage(true);
                    try {
                      const res = await findImage({ data: { productId: editing.id } });
                      if (res.image_url) {
                        setEditing({ ...editing, image_url: res.image_url });
                        toast.success("Found a product photo");
                      } else {
                        toast.error(res.error || "No photo found");
                      }
                    } catch (e) {
                      toast.error((e as Error).message);
                    } finally {
                      setFetchingImage(false);
                    }
                  }}
                >
                  <ImageIcon className="mr-1 h-4 w-4" /> {fetchingImage ? "Looking…" : "Find from URL"}
                </Button>
              </div>
            </Field>
            {editing.image_url ? (
              <img
                src={editing.image_url}
                alt={editing.description || editing.model}
                className="mt-3 h-28 w-28 rounded-md border border-border object-contain bg-white"
              />
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                Save the product first, then "Find from URL" pulls the photo off the vendor page.
              </p>
            )}
          </div>
        </div>
        <Button className="mt-6" onClick={save}>
          <Save className="mr-1 h-4 w-4" /> Save product
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Products</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your price list. Import a BidMagic/D-Tools XML library or a CSV price file, then pull items
            straight into proposals.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xml,.csv,text/csv,text/xml,application/xml"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <Button variant="outline" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Upload className="mr-1 h-4 w-4" /> {importing ? "Importing…" : "Import price file"}
          </Button>
          <Button
            variant="outline"
            disabled={fetchingImage}
            onClick={async () => {
              const targets = rows.filter((r) => r.product_url);
              if (targets.length === 0) return toast.info("There are no products with a vendor URL to repair.");
              setFetchingImage(true);
              let found = 0;
              for (const t of targets) {
                try {
                  const res = await findImage({ data: { productId: t.id } });
                  if (res.image_url) found++;
                } catch { /* keep going */ }
              }
              setFetchingImage(false);
              toast.success(`Found ${found} photo${found === 1 ? "" : "s"} of ${targets.length}`);
              load();
            }}
          >
            <ImageIcon className="mr-1 h-4 w-4" /> {fetchingImage ? "Finding photos…" : "Repair / find photos"}
          </Button>
          <Button onClick={() => setEditing(emptyProduct())}>
            <Plus className="mr-1 h-4 w-4" /> New product
          </Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search model, brand, description, category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
          No products yet. Log in to your dealer portal, download the price file, and import it here.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 p-3 hover:bg-accent">
              {p.image_url ? (
                <img src={p.image_url} alt={p.description || p.model} loading="lazy" className="h-12 w-12 shrink-0 rounded border border-border object-contain bg-white" />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-dashed border-border text-muted-foreground">
                  <ImageIcon className="h-4 w-4" />
                </div>
              )}
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => setEditing({ ...(p as unknown as ProductDraft), id: p.id })}
              >
                <div className="truncate font-medium">{p.description || p.model}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {[p.mfg_name, p.model, p.category].filter(Boolean).join(" · ")}
                </div>
              </button>
              <div className="shrink-0 text-right text-xs">
                <div className="font-semibold">${Number(p.sell_price).toFixed(2)}</div>
                <div className="text-muted-foreground">cost ${Number(p.unit_cost).toFixed(2)}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(p.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
